import {
  faSort,
  faSortAlphaAsc,
  faSortAlphaDesc,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { Cell } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { useNavigate } from "react-router";
import { LegacyButton as Button } from "web-ui/legacy";

import { getDuolingoTtsLanguage, getTtsHash } from "../../api/duolingoTts";
import styles from "../../styles/AudioGenerator/HistoryScreen.module.scss";
import type { StoredDuolingoTtsItem } from "../../util/duolingoTtsStorage";
import { getStoredDuolingoTtsHistory } from "../../util/duolingoTtsStorage";
import { UrlParameterNames } from "../../util/urlParameters";
import { Error } from "../Error";

/* eslint-disable  react/jsx-no-literals */

const columnHelper = createColumnHelper<StoredDuolingoTtsItem>();

const dateColumnId = "date";
const voiceColumnId = "voice";
const speedColumnId = "speed";
const textColumnId = "text";
const ssmlColumnId = "ssml";
const statusColumnId = "status";

const columns = [
  columnHelper.accessor(dateColumnId, {
    cell: info => info.renderValue(),
    header: "Date",
  }),
  columnHelper.accessor(voiceColumnId, {
    cell: info => info.renderValue(),
    header: "Voice",
  }),
  columnHelper.accessor(speedColumnId, {
    cell: info => info.renderValue(),
    header: "Speed",
  }),
  columnHelper.accessor(textColumnId, {
    cell: info => info.renderValue(),
    header: "Text",
  }),
  columnHelper.accessor(ssmlColumnId, {
    cell: info => info.renderValue(),
    header: "SSML",
  }),
  columnHelper.accessor(statusColumnId, {
    cell: info => info.renderValue(),
    header: "Status",
  }),
];

const data = getStoredDuolingoTtsHistory();

export const HistoryScreen = () => {
  const reactTable = useReactTable({
    columns,
    data,
    enableMultiSort: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ desc: true, id: dateColumnId }],
    },
    isMultiSortEvent: () => true,
  });
  const navigate = useNavigate();
  const getCellContent = (cell: Cell<StoredDuolingoTtsItem, unknown>) => (
    <td key={cell.id}>
      {cell.column.id === "status" && cell.getValue() === "created" ? (
        <Button
          onClick={async () => {
            const { voice, speed, text, ssml } = cell.row.original;
            const language = await getDuolingoTtsLanguage(voice);
            const ttsHash = getTtsHash(language, voice, speed, text, ssml);
            navigate(`/?${UrlParameterNames.ttsHash}=${ttsHash}`);
          }}
          role="link"
          variant="solid"
        >
          Go to result
        </Button>
      ) : (
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </td>
  );

  return (
    <div className={styles["container"]}>
      <h1>Audio Generation History</h1>
      {data.length > 0 ? (
        <table>
          <thead>
            {reactTable.getHeaderGroups().map(headerGroup => (
              <React.Fragment key={headerGroup.id}>
                <tr key={`${headerGroup.id}-heading`}>
                  {headerGroup.headers.map(header => (
                    <th colSpan={header.colSpan} key={`${header.id}-heading`}>
                      {header.isPlaceholder ? null : (
                        <span onClick={header.column.getToggleSortingHandler()}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}{" "}
                          {{
                            asc: <FontAwesomeIcon icon={faSortAlphaAsc} />,
                            desc: <FontAwesomeIcon icon={faSortAlphaDesc} />,
                            false: <FontAwesomeIcon icon={faSort} />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </React.Fragment>
            ))}
          </thead>
          <tbody>
            {reactTable.getRowModel().rows.map(row => (
              <tr key={row.id}>{row.getVisibleCells().map(getCellContent)}</tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Error subject="No previous Duolingo TTS generation history found!" />
      )}
    </div>
  );
};
